import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Facebook, Instagram, Youtube } from "lucide-react";
import TrustBadges from "@/components/TrustBadges";
import logoWhite from "@/assets/logo-white.png";
import { getTier1Services, services } from "@/data/services";
import { counties } from "@/data/locations";
import { organization } from "@/config/organization";

// Pinterest icon component (not in lucide-react)
const PinterestIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/>
  </svg>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const popularServices = getTier1Services();

  const webPlatforms = [
    { to: "/services/custom-business-solutions", label: "Custom Web Platforms" },
    { to: "/solutions/web-apps", label: "Web Applications" },
    { to: "/solutions/mobile-apps", label: "Mobile Apps" },
    { to: "/solutions/client-portals", label: "Client Portals" },
    { to: "/solutions/crm", label: "Custom CRM" },
    { to: "/services/ecommerce", label: "E-Commerce" },
    { to: "/services/web-design", label: "Stop Renting Software" },
  ];

  const marketingSEO = [
    { to: "/services/local-service-ads", label: "Local Service Ads" },
    { to: "/services/ppc", label: "Google Call Ads" },
    { to: "/services/seo", label: "Local SEO & Maps" },
    { to: "/services/gmb-optimization", label: "Google Business Profile" },
    { to: "/valentine-seo", label: "Valentine SEO Tools" },
    { to: "/funnel/local-seo", label: "Free Authority Scan" },
  ];

  const portfolioResources = [
    { to: "/live-websites", label: "Live Website Portfolio" },
    { to: "/portfolio", label: "Creative Archive" },
    { to: "/case-studies", label: "Case Studies" },
    { to: "/about", label: "About Us" },
    { to: "/blog", label: "Blog" },
    { to: "/faq", label: "FAQ" },
    { to: "/sitemap", label: "Sitemap" },
  ];

  // Top cities for footer service areas column
  const topCities = [
    { county: "huron-county", city: "bad-axe", name: "Bad Axe" },
    { county: "tuscola-county", city: "caro", name: "Caro" },
    { county: "sanilac-county", city: "sandusky", name: "Sandusky" },
    { county: "huron-county", city: "harbor-beach", name: "Harbor Beach" },
    { county: "tuscola-county", city: "cass-city", name: "Cass City" },
    { county: "huron-county", city: "pigeon", name: "Pigeon" },
    { county: "huron-county", city: "sebewaing", name: "Sebewaing" },
    { county: "sanilac-county", city: "lexington", name: "Lexington" },
  ];

  return (
    <footer className="relative bg-primary text-primary-foreground overflow-hidden">
      {/* Gradient top border */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-accent via-primary-foreground/20 to-accent" />
      
      {/* Main Footer */}
      <div className="container mx-auto px-4 py-12 md:py-16 relative z-10">
        <div className="space-y-8">
          {/* Company Info - Full width on mobile */}
          <div>
            <Link to="/" className="flex items-center mb-4 group">
              <img 
                src={logoWhite} 
                alt="Valentine Digital Marketing Agency" 
                className="h-12 w-auto max-w-[220px] group-hover:drop-shadow-[0_0_20px_rgba(109,213,237,0.3)] transition-all duration-300"
              />
            </Link>
            <p className="text-primary-foreground/70 text-sm mb-4 max-w-md">
              <span className="font-semibold text-accent">Local Roots, Global Reach.</span> Full-service digital platforms, high-performance SEO, and bespoke web architecture engineered for Michigan businesses and beyond.
            </p>
            <div className="flex gap-4 mb-6">
              {[
                { href: "https://www.facebook.com/valentinedigital", icon: Facebook, label: "Facebook" },
                { href: "https://www.instagram.com/valentinedigitalmarketing/", icon: Instagram, label: "Instagram" },
                { href: "https://www.youtube.com/@ValentineDigital", icon: Youtube, label: "YouTube" },
              ].map((social) => (
                <a 
                  key={social.label}
                  href={social.href} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-primary-foreground/70 hover:text-accent hover:scale-110 transition-all duration-300 min-w-[44px] min-h-[44px] flex items-center justify-center" 
                  aria-label={social.label}
                >
                  <social.icon className="h-5 w-5" />
                </a>
              ))}
              <a 
                href="https://www.pinterest.com/valentinedigitalmarketing/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-primary-foreground/70 hover:text-accent hover:scale-110 transition-all duration-300 min-w-[44px] min-h-[44px] flex items-center justify-center" 
                aria-label="Pinterest"
              >
                <PinterestIcon className="h-5 w-5" />
              </a>
              <a 
                href={organization.googleBusinessProfileUrl} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-primary-foreground/70 hover:text-accent hover:scale-110 transition-all duration-300 min-w-[44px] min-h-[44px] flex items-center justify-center" 
                aria-label="Google Business Profile"
              >
                <MapPin className="h-5 w-5" />
              </a>
            </div>
            
            {/* Contact Info */}
            <ul className="space-y-2">
              <li>
                <a href={organization.phoneHref} className="flex items-center gap-2 text-primary-foreground/70 hover:text-accent transition-colors text-sm group">
                  <Phone className="h-4 w-4 group-hover:scale-110 transition-transform" />
                  {organization.phoneDisplay}
                </a>
              </li>
              <li>
                <a href={organization.emailHref} className="flex items-center gap-2 text-primary-foreground/70 hover:text-accent transition-colors text-sm group">
                  <Mail className="h-4 w-4 group-hover:scale-110 transition-transform" />
                  {organization.email}
                </a>
              </li>
              <li>
                <div className="flex items-start gap-2 text-primary-foreground/70 text-sm">
                  <MapPin className="h-4 w-4 mt-0.5" />
                  <span>{organization.address.streetAddress}, {organization.address.addressLocality}, {organization.address.addressRegion} {organization.address.postalCode}</span>
                </div>
              </li>
            </ul>
          </div>

          {/* Navigation Links - 3 columns on desktop for absorbed structural depth */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 lg:gap-10 pt-4 border-t border-primary-foreground/10">
            {/* Web & Platforms */}
            <div>
              <h4 className="font-semibold mb-4 text-sm md:text-base text-accent">Web & Platforms</h4>
              <ul className="space-y-2">
                {webPlatforms.map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      className="text-primary-foreground/70 hover:text-accent hover:translate-x-1 transition-all duration-300 text-xs md:text-sm inline-block py-1 relative after:absolute after:bottom-0 after:left-0 after:h-[1px] after:w-0 after:bg-accent after:transition-all after:duration-300 hover:after:w-full"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Marketing & SEO */}
            <div>
              <h4 className="font-semibold mb-4 text-sm md:text-base text-accent">Marketing & SEO</h4>
              <ul className="space-y-2">
                {marketingSEO.map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      className="text-primary-foreground/70 hover:text-accent hover:translate-x-1 transition-all duration-300 text-xs md:text-sm inline-block py-1 relative after:absolute after:bottom-0 after:left-0 after:h-[1px] after:w-0 after:bg-accent after:transition-all after:duration-300 hover:after:w-full"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Portfolio & Resources */}
            <div>
              <h4 className="font-semibold mb-4 text-sm md:text-base text-accent">Portfolio & Resources</h4>
              <ul className="space-y-2">
                {portfolioResources.map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      className="text-primary-foreground/70 hover:text-accent hover:translate-x-1 transition-all duration-300 text-xs md:text-sm inline-block py-1 relative after:absolute after:bottom-0 after:left-0 after:h-[1px] after:w-0 after:bg-accent after:transition-all after:duration-300 hover:after:w-full"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Service Areas Row (SEO) - Hidden on mobile */}
        <div className="hidden md:block mt-10 pt-8 border-t border-primary-foreground/10">
          <p className="text-xs text-primary-foreground/50 mb-3 font-medium uppercase tracking-wider">Michigan Service Areas</p>
          <p className="text-sm text-primary-foreground/60 leading-relaxed mb-3">
            {counties.map((county, index) => (
              <span key={county.slug}>
                <Link
                  to={`/${county.slug}`}
                  className="hover:text-accent transition-colors font-medium"
                >
                  {county.name}
                </Link>
                {index < counties.length - 1 && <span className="mx-2">•</span>}
              </span>
            ))}
          </p>
          <p className="text-sm text-primary-foreground/60 leading-relaxed">
            {topCities.map((c, index) => (
              <span key={c.city}>
                <Link
                  to={`/${c.county}/${c.city}`}
                  className="hover:text-accent transition-colors"
                >
                  Digital marketing in {c.name}
                </Link>
                {index < topCities.length - 1 && <span className="mx-2">•</span>}
              </span>
            ))}
          </p>
        </div>

        {/* All Services Row (SEO) - Hidden on mobile */}
        <div className="hidden md:block mt-6 pt-6 border-t border-primary-foreground/10">
          <p className="text-xs text-primary-foreground/50 mb-3 font-medium uppercase tracking-wider">All Services</p>
          <p className="text-sm text-primary-foreground/60 leading-relaxed">
            {services.map((service, index) => (
              <span key={service.id}>
                <Link 
                  to={`/services/${service.id}`}
                  className="hover:text-accent transition-colors"
                >
                  {service.title}
                </Link>
                {index < services.length - 1 && <span className="mx-2">•</span>}
              </span>
            ))}
          </p>
        </div>

        {/* Trust Badges */}
        <div className="mt-8 pt-8 border-t border-primary-foreground/20">
          <TrustBadges variant="dark" />
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-primary-foreground/10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-primary-foreground/60">
            <p>© {currentYear} Valentine Digital. All rights reserved.</p>
            <div className="flex gap-6">
              <Link to="/privacy" className="hover:text-accent transition-colors py-1">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-accent transition-colors py-1">Terms of Service</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
